/** Keep independently released dashboard and public/auth pages on their own bundles. */
interface BridgeState { references: number; dispose: () => void }
type BridgeWindow = Window & { __tcShellRoutingBridge?: BridgeState };
type ReloadNavigation = { reload: () => { committed?: Promise<unknown>; finished: Promise<unknown> } };

export const installShellRoutingBridge = (): (() => void) => {
  const target = window as BridgeWindow;
  const existing = target.__tcShellRoutingBridge;
  if (existing) {
    existing.references++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (--existing.references === 0) existing.dispose();
    };
  }

  const isDashboard = (path: string) => path === '/dashboard' || path.startsWith('/dashboard/');
  const dashboardShell = document.documentElement.dataset.tcShell === 'dashboard'
    || isDashboard(location.pathname);
  const push = history.pushState;
  const replace = history.replaceState;
  const navigation = (window as unknown as { navigation?: ReloadNavigation }).navigation;
  const canObserveReload = typeof navigation?.reload === 'function';
  let navigating = false;
  let ownedEntry = { href: location.href, state: history.state };
  let attempt = 0;
  let hiddenRoot: HTMLElement | null = null;
  let previousVisibility = '';

  const restoreRoot = () => {
    if (hiddenRoot) hiddenRoot.style.visibility = previousVisibility;
    hiddenRoot = null;
  };
  const check = () => {
    if (isDashboard(location.pathname) === dashboardShell) {
      ownedEntry = { href: location.href, state: history.state };
      navigating = false;
      attempt++;
      restoreRoot();
      return;
    }
    if (navigating) return;
    navigating = true;
    hiddenRoot = document.getElementById('root');
    if (hiddenRoot) {
      previousVisibility = hiddenRoot.style.visibility;
      hiddenRoot.style.visibility = 'hidden';
    }
    const currentAttempt = ++attempt;
    const recover = () => {
      if (!navigating || currentAttempt !== attempt) return;
      replace.call(history, ownedEntry.state, '', ownedEntry.href);
      navigating = false;
      restoreRoot();
      window.dispatchEvent(new PopStateEvent('popstate', { state: ownedEntry.state }));
    };
    if (canObserveReload) {
      // A prevented unload event also occurs when the user accepts its dialog.
      // Only the Navigation API's rejection confirms the document survived.
      try {
        const result = navigation!.reload();
        result.committed?.catch(() => undefined);
        result.finished.catch(recover);
      } catch { recover(); }
    } else {
      // Back/forward already changed the URL. Restore the surviving entry first
      // so a canceled native document navigation cannot strand an unsaved form.
      const destination = location.href;
      replace.call(history, ownedEntry.state, '', ownedEntry.href);
      navigating = false;
      restoreRoot();
      location.replace(destination);
    }
  };
  const nativeCrossing = (url: string | URL | null | undefined, mode: 'push' | 'replace') => {
    if (canObserveReload || url == null) return false;
    let destination: URL;
    try { destination = new URL(url, location.href); } catch { return false; }
    if (destination.origin !== location.origin
      || isDashboard(destination.pathname) === dashboardShell) return false;
    // Older browsers navigate before mutating History. Canceling their native
    // unload prompt leaves the form, URL and state untouched.
    if (mode === 'push') location.assign(destination.href);
    else location.replace(destination.href);
    return true;
  };
  const wrappedPush: History['pushState'] = function (...args) {
    if (this === history && nativeCrossing(args[2], 'push')) return;
    push.apply(this, args);
    check();
  };
  const wrappedReplace: History['replaceState'] = function (...args) {
    if (this === history && nativeCrossing(args[2], 'replace')) return;
    replace.apply(this, args);
    check();
  };
  const onPageShow = () => {
    if (isDashboard(location.pathname) === dashboardShell) {
      navigating = false;
      restoreRoot();
    } else check();
  };
  history.pushState = wrappedPush;
  history.replaceState = wrappedReplace;
  window.addEventListener('popstate', check);
  window.addEventListener('pageshow', onPageShow);

  const state: BridgeState = {
    references: 1,
    dispose: () => {
      if (history.pushState === wrappedPush) history.pushState = push;
      if (history.replaceState === wrappedReplace) history.replaceState = replace;
      window.removeEventListener('popstate', check);
      window.removeEventListener('pageshow', onPageShow);
      attempt++;
      navigating = false;
      restoreRoot();
      if (target.__tcShellRoutingBridge === state) delete target.__tcShellRoutingBridge;
    },
  };
  target.__tcShellRoutingBridge = state;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--state.references === 0) state.dispose();
  };
};
